<script lang="ts">
  import { createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { catalogApi } from '$lib/api/catalog';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import type { CatalogCategory } from '$lib/types';

  const {
    mode,
    category,
    allCategories,
    onClose
  }: {
    mode: 'rename' | 'merge';
    category: CatalogCategory;
    allCategories: CatalogCategory[];
    onClose: () => void;
  } = $props();

  const queryClient = useQueryClient();
  let newName = $state('');
  let target = $state('');
  let error = $state('');

  const otherCategories = $derived(allCategories.filter((c) => c.category !== category.category));

  $effect(() => {
    newName = category.category;
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['catalog-categories'] });
    queryClient.invalidateQueries({ queryKey: ['catalog-health'] });
    queryClient.invalidateQueries({ queryKey: ['catalog-products'] });
  }

  const renameMutation = createMutation(() => ({
    mutationFn: () => catalogApi.renameCategory(category.category, newName.trim()),
    onSuccess: (data) => {
      invalidate();
      toast.success(`Renamed — ${data.moved} product${data.moved === 1 ? '' : 's'} updated.`);
      onClose();
    },
    onError: (err: unknown) => (error = getErrorMessage(err))
  }));

  const mergeMutation = createMutation(() => ({
    mutationFn: () => catalogApi.mergeCategory(category.category, target),
    onSuccess: (data) => {
      invalidate();
      toast.success(`Merged — ${data.moved} product${data.moved === 1 ? '' : 's'} moved into "${data.into}".`);
      onClose();
    },
    onError: (err: unknown) => (error = getErrorMessage(err))
  }));

  function submit(e: SubmitEvent) {
    e.preventDefault();
    error = '';
    if (mode === 'rename') {
      if (!newName.trim()) {
        error = 'New name is required.';
        return;
      }
      renameMutation.mutate();
    } else {
      if (!target) {
        error = 'Pick a target category.';
        return;
      }
      mergeMutation.mutate();
    }
  }

  const pending = $derived(mode === 'rename' ? renameMutation.isPending : mergeMutation.isPending);
</script>

<Modal title={mode === 'rename' ? 'Rename category' : 'Merge category'} onClose={pending ? () => {} : onClose}>
  <form class="space-y-4" onsubmit={submit}>
    <p class="tym-small">
      {category.category || 'Uncategorised'} &middot; {category.count} product{category.count === 1 ? '' : 's'}
    </p>

    {#if mode === 'rename'}
      <div>
        <label class="label" for="new-name">New name</label>
        <input id="new-name" class="input" bind:value={newName} />
      </div>
      <p class="tym-small">Renames the category on all {category.count} product{category.count === 1 ? '' : 's'} in it.</p>
    {:else}
      <div>
        <label class="label" for="merge-target">Merge into</label>
        <select id="merge-target" class="input" bind:value={target}>
          <option value="">-- choose a target category --</option>
          {#each otherCategories as c (c.category)}
            <option value={c.category}>{c.category} ({c.count})</option>
          {/each}
        </select>
      </div>
      {#if target}
        <p class="tym-small">
          This moves all <strong>{category.count}</strong> product{category.count === 1 ? '' : 's'} out of
          "{category.category}" into "{target}". "{category.category}" will no longer exist. This can't be undone
          automatically.
        </p>
      {/if}
    {/if}

    {#if error}<p class="tym-error-state">{error}</p>{/if}

    <div class="flex justify-end gap-2 pt-2">
      <button type="button" class="btn-secondary" onclick={onClose} disabled={pending}>Cancel</button>
      <button type="submit" class="btn-primary" disabled={pending}>
        {pending ? 'Saving…' : mode === 'rename' ? 'Rename' : 'Merge'}
      </button>
    </div>
  </form>
</Modal>
